import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  CreateWeeklySummaryRequest,
  getNewWeeklySummaryState,
  getUpdateWeeklySummaryState,
  SummaryAction,
  SummaryChallenge,
  SummaryGoal,
  SummaryIssueIdentify,
  SummaryReason,
  SummarySupportResource,
  UpdateWeeklySummaryRequest,
  WeeklySummaryResponse,
} from "../../../redux/features/weekly-summary-slice";
import Button from "../../../components/button";
import ReasonForm from "./summary-forms/reason-form";
import ChallengeForm from "./summary-forms/challenge-form";
import GoalForm from "./summary-forms/goal-form";
import SupportResourceForm from "./summary-forms/support-resource-form";
import IssueIdentifyForm from "./summary-forms/issue-identify-form";
import SwitchTab from "../../../components/switch-tab";
import { summaryOptions } from "../../../core/utils/constants";
import Input from "../../../components/input";

interface Props {
  summary: WeeklySummaryResponse | null;
  startDate: Date;
  endDate: Date;
  selectedOption: string;
  setSelectedOption: (value: string) => void;
  onSubmit: (values: CreateWeeklySummaryRequest) => void;
  onUpdate: (value: UpdateWeeklySummaryRequest) => void;
  onRefresh: () => void;
}

export default function SummarySubmitForm(props: Props) {
  const {
    summary,
    startDate,
    endDate,
    selectedOption,
    setSelectedOption,
    onSubmit,
    onUpdate,
    onRefresh,
  } = props;

  const newWeeklySummaryState = useSelector(getNewWeeklySummaryState);
  const updateWeeklySummaryState = useSelector(getUpdateWeeklySummaryState);

  const [metGoals, setMetGoals] = useState(false);
  const [reasons, setReasons] = useState<SummaryReason[]>([]);
  const [challenges, setChallenges] = useState<SummaryChallenge[]>([]);
  const [issues, setIssues] = useState<SummaryIssueIdentify[]>([]);
  const [goals, setGoals] = useState<SummaryGoal[]>([]);
  const [supportResources, setSupportResources] = useState<
    SummarySupportResource[]
  >([]);

  const renderMenuItem = (label: string, disabled: boolean = false) => {
    return (
      <label
        className={`w-fit p-3 mb-2 text-sm hover:bg-gray-100 rounded-lg cursor-pointer ${
          selectedOption === label ? "bg-gray-100" : "bg-background-paper"
        } ${disabled ? "text-action-disabled" : "text-text-title"}`}
        onClick={() => !disabled && setSelectedOption(label)}
      >
        {label}
      </label>
    );
  };

  const renderForm = () => {
    switch (selectedOption) {
      case summaryOptions[0]:
        return (
          <ReasonForm
            key="reasonsForm"
            summary={summary}
            reasons={reasons}
            setReasons={setReasons}
            onRefresh={onRefresh}
          />
        );
      case summaryOptions[1]:
        return (
          <ChallengeForm
            key="challengesForm"
            summary={summary}
            challenges={challenges}
            setChallenges={setChallenges}
            onRefresh={onRefresh}
          />
        );
      case summaryOptions[2]:
        return (
          <IssueIdentifyForm
            key="issuesForm"
            summary={summary}
            issues={issues}
            setIssues={setIssues}
            onRefresh={onRefresh}
          />
        );
      case summaryOptions[3]:
        return (
          <GoalForm
            key="goalsForm"
            summary={summary}
            goals={goals}
            setGoals={setGoals}
            onRefresh={onRefresh}
          />
        );
      case summaryOptions[4]:
        return (
          <SupportResourceForm
            key="resourcesForm"
            summary={summary}
            resources={supportResources}
            setResources={setSupportResources}
            onRefresh={onRefresh}
          />
        );
      case "Leader Actions":
        return (
          <div key="actionsForm" className="flex flex-col rounded-lg space-y-4">
            {summary?.actions.map((action: SummaryAction) => (
              <div
                key={action.type}
                className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="text-xs text-text-title">Type</label>
                    <Input type="text" value={action.type} readonly />
                  </div>
                  <div>
                    <label className="text-xs text-text-title">Detail</label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={action.detail}
                      readonly
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
    }
  };

  useEffect(() => {
    if (summary) {
      setMetGoals(summary.metGoals);
      setReasons(summary.reasons);
      setChallenges(summary.challenges);
      setIssues(summary.issueIdentifies);
      setGoals(summary.goals);
      setSupportResources(summary.supportResources);
    } else {
      setMetGoals(false);
      setReasons([]);
      setChallenges([]);
      setIssues([]);
      setGoals([]);
      setSupportResources([]);
    }
  }, [summary]);

  useEffect(() => {
    if (selectedOption === summaryOptions[0] && metGoals) {
      setSelectedOption(summaryOptions[1]);
    }
  }, [metGoals]);

  return (
    <div className="flex flex-col h-full">
      <div className="h-full flex flex-col space-y-6 overflow-y-hidden">
        {/* Met goals */}
        <div className="flex items-center space-x-4">
          <label className="text-md font-medium">
            Did you meet output expectations?
          </label>
          <SwitchTab
            options={["Yes", "No"]}
            tab={metGoals ? "Yes" : "No"}
            setTab={(value: string) => setMetGoals(value === "Yes")}
          />
        </div>
        <div className="h-full grid grid-cols-6 gap-4 overflow-hidden">
          <div className="h-full col-span-2 flex flex-col overflow-auto">
            <div className="h-full flex flex-col">
              {renderMenuItem(summaryOptions[0], metGoals)}
              {renderMenuItem(summaryOptions[1])}
              {renderMenuItem(summaryOptions[2])}
              {renderMenuItem(summaryOptions[3])}
              {renderMenuItem(summaryOptions[4])}
              {(summary?.actions.length ?? 0) > 0 &&
                renderMenuItem("Leader Actions")}
            </div>
            <Button
              label={summary ? "Save Changes" : "Submit"}
              loading={
                newWeeklySummaryState.loading ||
                updateWeeklySummaryState.loading
              }
              disabled={
                (!metGoals && reasons.length === 0) ||
                goals.length === 0 ||
                challenges.length === 0 ||
                issues.length === 0 ||
                supportResources.length === 0
              }
              onClick={() => {
                if (summary) {
                  onUpdate({
                    id: summary.id,
                    metGoals,
                    reasons: metGoals ? [] : reasons,
                    challenges,
                    issueIdentifies: issues,
                    goals,
                    supportResources,
                  });
                } else {
                  onSubmit({
                    metGoals,
                    reasons: metGoals ? [] : reasons,
                    challenges,
                    issueIdentifies: issues,
                    goals,
                    supportResources,
                    comments: [],
                    startDate: new Date(startDate.setHours(8, 0, 0, 0)),
                    endDate: new Date(endDate.setHours(17, 0, 0, 0)),
                  });
                }
              }}
              exClassName="w-fit"
            />
          </div>
          <div className="h-full col-span-4 flex flex-col overflow-y-scroll">
            {renderForm()}
          </div>
        </div>
      </div>
    </div>
  );
}
